/* eslint-disable no-param-reassign */
import { ExternalSyncError } from '@common/entities';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { resetStoreAction } from 'state/commonActions';

export interface StepStatus {
  errorCount?: number;
  touched?: boolean;
  complete?: boolean;
}

export type NavigationState = {
  currentTabIndex: number;
  leadProfileResponseId?: string;
  activeStepByTab: { [tabIndex: number]: string };
  stepStatus: { [tabIndex: number]: { [stepId: string]: StepStatus } };
  leasingJourneyErrors: (number | null)[];
  externalSyncErrorsByTab?: Record<number, Record<string, ExternalSyncError[]>>;
  modalNavigationActiveStep: number;
  activePageId?: string | null;
  previewModeActiveStep: number;
  mobileCartOpenRequested?: boolean;
};

const initialState: NavigationState = {
  currentTabIndex: 0,
  leadProfileResponseId: undefined,
  activeStepByTab: {},
  stepStatus: {},
  leasingJourneyErrors: [],
  externalSyncErrorsByTab: {},
  modalNavigationActiveStep: 0,
  activePageId: null,
  previewModeActiveStep: 0,
  mobileCartOpenRequested: false,
};

export const navigationSlice = createSlice({
  name: 'navigation',
  initialState,
  reducers: {
    setCurrentTabIndex: (state, action: PayloadAction<number>) => {
      state.currentTabIndex = action.payload;
      if (state.activeStepByTab[action.payload] === undefined) {
        state.activeStepByTab[action.payload] = '';
      }
    },
    setLeadProfileResponseId: (state, action: PayloadAction<string | undefined>) => {
      state.leadProfileResponseId = action.payload;
    },
    setActiveStep: (state, action: PayloadAction<{ tabIndex: number; step: string }>) => {
      const { tabIndex, step } = action.payload;
      state.activeStepByTab[tabIndex] = step;
    },
    resetActiveStepForTab: (state, action: PayloadAction<number>) => {
      state.activeStepByTab[action.payload] = '';
    },
    updateStepStatus: (
      state,
      action: PayloadAction<{
        tabIndex: number;
        step: string;
        status: StepStatus;
      }>,
    ) => {
      const { tabIndex, step, status } = action.payload;
      if (!state.stepStatus[tabIndex]) {
        state.stepStatus[tabIndex] = {};
      }

      state.stepStatus[tabIndex][step] = status;
    },
    setLeasingJourneyError: (
      state,
      action: PayloadAction<{ pages: any[]; errors: (number | null)[] }>,
    ) => {
      const { errors } = action.payload;
      return { ...state, leasingJourneyErrors: errors };
    },
    updateLeasingJourneyErrors: (
      state,
      action: PayloadAction<{ pageIndex: number; count: number | null }>,
    ) => {
      const { pageIndex, count } = action.payload;
      const updatedErrors = state.leasingJourneyErrors.map((errorCount, index) => {
        if (index === pageIndex) {
          return count;
        }
        return errorCount;
      });
      return { ...state, leasingJourneyErrors: updatedErrors };
    },
    setExternalSyncErrors: (
      state,
      action: PayloadAction<{ tabIndex: number; pageId: string; errors: ExternalSyncError[] }>,
    ) => {
      const { tabIndex, pageId, errors } = action.payload;
      state.externalSyncErrorsByTab ??= {};
      state.externalSyncErrorsByTab[tabIndex] ??= {};
      if (errors.length > 0) state.externalSyncErrorsByTab[tabIndex][pageId] = errors;
      else delete state.externalSyncErrorsByTab[tabIndex][pageId];
    },
    replaceExternalSyncErrorsForTab: (
      state,
      action: PayloadAction<{
        tabIndex: number;
        errorsByPageId: Record<string, ExternalSyncError[]>;
      }>,
    ) => {
      const { tabIndex, errorsByPageId } = action.payload;
      state.externalSyncErrorsByTab ??= {};
      state.externalSyncErrorsByTab[tabIndex] = errorsByPageId;
    },
    setActivePageId: (state, action: PayloadAction<string | null>) => {
      state.activePageId = action.payload;
    },
    updateModalNavigationActiveStep: (state, action: PayloadAction<number>) => {
      return { ...state, modalNavigationActiveStep: action.payload };
    },
    updatePreviewModeActiveStep: (state, action: PayloadAction<number>) => {
      return { ...state, previewModeActiveStep: action.payload };
    },
    requestMobileCartOpen: (state) => {
      state.mobileCartOpenRequested = true;
    },
    clearMobileCartOpenRequest: (state) => {
      state.mobileCartOpenRequested = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStoreAction, () => initialState);
  },
});

export const {
  resetActiveStepForTab,
  setActiveStep,
  setCurrentTabIndex,
  setLeadProfileResponseId,
  setLeasingJourneyError,
  updateLeasingJourneyErrors,
  setExternalSyncErrors,
  replaceExternalSyncErrorsForTab,
  setActivePageId,
  updateModalNavigationActiveStep,
  updateStepStatus,
  updatePreviewModeActiveStep,
  requestMobileCartOpen,
  clearMobileCartOpenRequest,
} = navigationSlice.actions;

export default navigationSlice.reducer;
